import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@material-ui/lab/Pagination";
import Tooltip from "@material-ui/core/Tooltip";
import { HourglassEmpty, Autorenew, CheckCircle } from "@material-ui/icons";
import theme from "../../theme";
import {
  getVendorSettlementsAction,
} from "../../containers/vendorDashboard/action";
import { moneyValue } from "../../utils";
import Loading from "../loading";
import moment from "moment";
import CommonTable from "../table";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.colors.White,
    width: "100%",
    minHeight: "calc(100vh - 185px)",
    padding: "45px 90px",
    marginLeft: "220px",
  },
  headerContainer: {
    marginBottom: "30px",
    "& .title": {
      color: theme.colors.Violet,
      fontFamily: theme.fontFamily.MARKPROMEDIUM,
      fontSize: "24px",
    },
  },
  section: {
    marginBottom: "40px",
    "& .sectionTitle": {
      color: theme.colors.Violet,
      fontSize: "20px",
      fontFamily: theme.fontFamily.MARKPROBOLD,
      marginBottom: "20px",
    },
  },
  tableContainer: {
    marginTop: "20px",
  },
  headerName: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "14px",
  },
  headerBody: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPRO,
    fontSize: "14px",
  },
  amount: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPRO,
    fontSize: "14px",
    "& .currency": {
      fontSize: "12px",
      marginRight: "4px",
    },
  },
  statusBadge: {
    display: "inline-block",
    padding: "8px 20px",
    borderRadius: "20px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    textTransform: "uppercase",
  },
  statusPending: {
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.DodgerBlue,
  },
  statusProcessing: {
    backgroundColor: theme.colors.DodgerBlue,
    color: theme.colors.White,
  },
  statusCompleted: {
    backgroundColor: theme.colors.MintGreen,
    color: theme.colors.Violet,
  },
  scheduleInfo: {
    backgroundColor: theme.colors.Zumthor,
    borderRadius: "10px",
    padding: "20px 30px",
    marginBottom: "30px",
    "& .label": {
      color: theme.colors.Violet,
      fontSize: "14px",
      fontFamily: theme.fontFamily.MARKPRO,
      marginBottom: "5px",
    },
    "& .value": {
      color: theme.colors.Violet,
      fontSize: "18px",
      fontFamily: theme.fontFamily.MARKPROBOLD,
    },
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
    marginBottom: "20px",
  },
}));

function VendorSettlements() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const getVendorSettlementsMethod = useCallback(
    (data) => dispatch(getVendorSettlementsAction(data)),
    [dispatch]
  );

  const vendorDashboardReducer = useSelector((state) => state.vendorDashboardReducer);
  const vendorSettlements = vendorDashboardReducer?.vendorSettlements || {
    count: 0,
    next: null,
    previous: null,
    data: [],
  };

  // Fetch settlements with pagination
  useEffect(() => {
    getVendorSettlementsMethod({
      page: currentPage,
      page_size: pageSize,
    });
  }, [getVendorSettlementsMethod, currentPage, pageSize]);

  const settlements = vendorSettlements?.data || [];
  const upcomingSettlements = settlements.filter(
    (s) => s.status?.toLowerCase() === "pending" || s.status?.toLowerCase() === "processing"
  );
  const pastSettlements = settlements.filter((s) => s.status?.toLowerCase() === "completed");

  // Calculate total pages from API response
  const totalPages = Math.ceil((vendorSettlements?.count || 0) / pageSize);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || "";
    let IconComponent = null;
    let iconColor = "";
    let tooltipTitle = status || "N/A";

    if (statusLower === "pending") {
      IconComponent = HourglassEmpty;
      iconColor = theme.colors.DodgerBlue;
    } else if (statusLower === "processing") {
      IconComponent = Autorenew;
      iconColor = theme.colors.DodgerBlue;
    } else if (statusLower === "completed") {
      IconComponent = CheckCircle;
      iconColor = theme.colors.MintGreen;
    }

    if (!IconComponent) {
      return null;
    }

    return (
      <Tooltip title={tooltipTitle} arrow>
        <IconComponent
          style={{
            color: iconColor,
            fontSize: "20px",
            cursor: "pointer"
          }}
        />
      </Tooltip>
    );
  };

  const schedule = vendorDashboardReducer?.vendorProfile?.vendor?.settlement_schedules || {
    frequency: "daily",
    next_date_settlement: null,
  };
  return (
    <div className={classes.root}>
      <Loading showLoader={vendorDashboardReducer?.loading} />
      {vendorDashboardReducer?.error?.length ? (
        <div className={"error-container"}>
          {Array.isArray(vendorDashboardReducer.error)
            ? vendorDashboardReducer.error.map((each, index) => (
              <div key={index}>{each}</div>
            ))
            : <div>{vendorDashboardReducer.error}</div>}
        </div>
      ) : null}

      <div className={classes.headerContainer}>
        <div className="title">Settlements</div>
      </div>

      <div className={classes.scheduleInfo}>
        <div className="label">Settlement Schedule</div>
        <div className="value">
          {schedule.frequency?.toLowerCase() === "daily"
            ? "Daily"
            : schedule.frequency?.toLowerCase() === "weekly"
              ? "Weekly"
              : "Custom"}
        </div>
        {schedule.next_date_settlement && (
          <>
            <div className="label" style={{ marginTop: "10px" }}>
              Next Settlement Date
            </div>
            <div className="value">
              {moment(schedule.next_date_settlement).format("DD/MM/YYYY")}
            </div>
          </>
        )}
      </div>

      <div className={classes.section}>
        <div className="sectionTitle">Upcoming Settlements</div>
        <div className={classes.tableContainer}>
          <CommonTable
            columns={[
              {
                Header: "Payment Reference",
                accessor: "payment_reference",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data) => {
                  return (
                    <div className={classes.headerBody}>
                      {data || "N/A"}
                    </div>
                  );
                },
              },
              {
                Header: "Charge Amount",
                accessor: "total_discount",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const chargeAmount = record?.charge_amount || record?.total_discount || 0;
                  return (
                    <div className={classes.amount}>
                      <span className={classes.currency}>{record?.currency || "NGN"}</span>
                      {moneyValue(chargeAmount)}
                    </div>
                  );
                },
              },
              {
                Header: "Amount",
                accessor: "net_amount",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  return (
                    <div className={classes.amount}>
                      <span className={classes.currency}>{record?.currency || "NGN"}</span>
                      {moneyValue(record?.net_amount || 0)}
                    </div>
                  );
                },
              },
              {
                Header: "Status",
                accessor: "status",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const statusIcon = getStatusIcon(record?.status);
                  if (!statusIcon) {
                    return <div className={classes.headerBody}>N/A</div>;
                  }
                  return statusIcon;
                },
              },
              {
                Header: "Date",
                accessor: "processed_at",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const date = record?.processed_at || record?.created_at;
                  return (
                    <div className={classes.headerBody}>
                      {date
                        ? moment(date).format("DD/MM/YYYY HH:mm")
                        : "Scheduled"}
                    </div>
                  );
                },
              },
            ]}
            data={upcomingSettlements}
            loading={vendorDashboardReducer?.loading}
          />
        </div>
      </div>

      <div className={classes.section}>
        <div className="sectionTitle">Settlement History</div>
        <div className={classes.tableContainer}>
          <CommonTable
            columns={[
              {
                Header: "Payment Reference",
                accessor: "payment_reference",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data) => {
                  return (
                    <div className={classes.headerBody}>
                      {data || "N/A"}
                    </div>
                  );
                },
              },
              {
                Header: "Charge Amount",
                accessor: "total_discount",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const chargeAmount = record?.charge_amount || record?.total_discount || 0;
                  return (
                    <div className={classes.amount}>
                      <span className={classes.currency}>{record?.currency || "NGN"}</span>
                      {moneyValue(chargeAmount)}
                    </div>
                  );
                },
              },
              {
                Header: "Amount",
                accessor: "net_amount",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  return (
                    <div className={classes.amount}>
                      <span className={classes.currency}>{record?.currency || "NGN"}</span>
                      {moneyValue(record?.net_amount || 0)}
                    </div>
                  );
                },
              },
              {
                Header: "Status",
                accessor: "status",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const statusIcon = getStatusIcon(record?.status);
                  if (!statusIcon) {
                    return <div className={classes.headerBody}>N/A</div>;
                  }
                  return statusIcon;
                },
              },
              {
                Header: "Date",
                accessor: "completed_at",
                HeaderCell: (data) => {
                  return <div className={classes.headerName}>{data}</div>;
                },
                Cell: (data, record) => {
                  const date = record?.completed_at || record?.processed_at;
                  return (
                    <div className={classes.headerBody}>
                      {date
                        ? moment(date).format("DD/MM/YYYY HH:mm")
                        : "N/A"}
                    </div>
                  );
                },
              },
            ]}
            data={pastSettlements}
            loading={vendorDashboardReducer?.loading}
          />
        </div>
      </div>

      {totalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination
            count={totalPages}
            page={currentPage}
            showFirstButton
            showLastButton
            onChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}

export default VendorSettlements;
